import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { get_order, cancel_order, messageClear } from "../../store/reducers/orderReducer";
import toast from "react-hot-toast";
import { getProxiedUrl } from '../../utils/imageUtils'

const Order = ({ sellerId, sellerName }) => {
  const { orderNumber } = useParams();
  const dispatch = useDispatch();
  const { myOrder, loading, errorMessage, successMessage } = useSelector((state) => state.order);
  const { userInfo } = useSelector((state) => state.auth);
  const [currencySymbol, setCurrencySymbol] = useState("");

  useEffect(() => {
    if (orderNumber) {
      dispatch(get_order(orderNumber));
    }
  }, [orderNumber, dispatch]);

  useEffect(() => {
    if (myOrder && myOrder.seller && myOrder.seller.currency) {
      setCurrencySymbol(myOrder.seller.currency.symbol || "");
    }
  }, [myOrder]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [successMessage, errorMessage, dispatch]);

  const handleCancelOrder = async () => {
    if (window.confirm('Are you sure you want to cancel this order?')) {
      dispatch(cancel_order({ orderNumber }));
    }
  };

  if (loading) {
    return <div className="bg-white p-5">Loading order details...</div>;
  }

  if (errorMessage) {
    return (
      <div className="bg-white p-5">
        <div className="text-red-500">{errorMessage}</div>
        <Link to={`/${sellerName}/dashboard/my-orders`} className="text-blue-500 hover:underline">
          Back to Orders
        </Link>
      </div>
    );
  }

  if (!myOrder || Object.keys(myOrder).length === 0) {
    return (
      <div className="bg-white p-5">
        <div>No order details found</div>
        <Link to={`/${sellerName}/dashboard/my-orders`} className="text-blue-500 hover:underline">
          Back to Orders
        </Link>
      </div>
    );
  }

  // Check if order can be cancelled
  const canCancelOrder = ['pending', 'placed', 'processing'].includes(myOrder.delivery_status?.toLowerCase()) && 
                        myOrder.customerId === userInfo?.id;

  const calculateDiscountedPrice = (product) => {
    if (!product) return 0;

    // If discountPrice is available and valid, use it instead of recalculating
    if (product.discountPrice && product.discountPrice > 0) {
      return product.discountPrice;
    }

    // Fallback to original calculation if no discountPrice is stored
    const { price = 0, discount = 0, bulkPricing = [] } = product;
    const quantity = product.quantity || 1;

    // First check for bulk pricing
    if (bulkPricing && bulkPricing.length > 0) {
      // Filter out invalid bulk prices and sort by quantity
      const validBulkPricing = bulkPricing
        .filter(bp => bp.quantity != null && bp.price != null)
        .sort((a, b) => b.quantity - a.quantity);

      // Find applicable bulk price
      const applicableBulkPrice = validBulkPricing.find(
        bp => quantity >= bp.quantity
      );

      if (applicableBulkPrice && applicableBulkPrice.price) {
        // For bulk pricing, we don't apply additional discount
        return applicableBulkPrice.price * quantity;
      }
    }

    // If no bulk pricing applies, calculate regular price with discount
    // Only apply discount if it's not already discounted (bulk price)
    const finalPrice = discount > 0 ? price - (price * discount / 100) : price;
    return finalPrice * quantity;
  };


  const CartItem = ({ product }) => {
    if (!product) return null;
  
  
    // Extract base price and price details
    const basePrice = product.price || 0;
    const originalPrice = product.originalPrice || basePrice;
    const discountedPrice = product.discountedPrice || basePrice;
    const priceDetails = product.priceDetails || {};
    const currency = product.currency || "NA";
    const quantity = product.quantity || 1;
    
    // Calculate addons total price
    const addonsTotalPrice = product.addons?.reduce((total, addon) => 
      total + (addon.price || 0), 0) || 0;
    
    // Check if bulk pricing was applied
    const isBulkPricingApplied = product.bulkPricing && product.bulkPricing.length > 0 && 
      product.bulkPricing.some(bp => quantity >= bp.quantity);
    
    // Calculate savings from bulk pricing
    let bulkSavings = 0;
    let bulkSavingsPercentage = 0;
    if (isBulkPricingApplied && originalPrice > discountedPrice) {
      bulkSavings = (originalPrice - discountedPrice) * quantity;
      bulkSavingsPercentage = ((originalPrice - discountedPrice) / originalPrice * 100).toFixed(2);
    }

    // Calculate total price including quantity
    const totalPriceWithQuantity = discountedPrice * quantity;
    const originalTotalPrice = originalPrice * quantity;
  
    return (
      <div className="w-full flex flex-wrap mb-4 p-4 bg-white rounded-lg shadow-sm">
        <div className="flex sm:w-full gap-2 w-7/12">
          <div className="flex gap-2 justify-start items-center">
            <img
              className="w-[80px] h-[80px] object-cover rounded"
              src={getProxiedUrl(product.images?.[0] || product.productInfo?.images?.[0] || "/placeholder-image.jpg")}
              alt={product.name || product.productInfo?.name || "Product image"}
            />
            <div className="pr-4 text-slate-600">
              <h2 className="text-md font-semibold">
                {product.name || product.productInfo?.name || "Unnamed Product"}
              </h2>
              <span className="text-sm">
                Brand: {product.brand || product.productInfo?.brand || "Unknown"}
              </span>
              {product.addons && product.addons.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium text-gray-700">Addons:</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {product.addons.map((addon, index) => (
                      <span
                        key={index}
                        className="text-xs bg-blue-100 text-blue-800 rounded-full px-2 py-1"
                      >
                        {addon.text || ""} ({currency}
                        {(addon.price || 0).toFixed(2)})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end w-5/12 sm:w-full sm:mt-3">
          <div className="pl-4 sm:pl-0 text-right">
            <div className="flex flex-col">
              {/* Current Price (Regular Price) */}
              <h2 className="text-lg font-bold text-green-600">
                {currency}{discountedPrice.toFixed(2)}
                <span className="text-sm text-blue-600 ml-2">(Regular Price)</span>
              </h2>
              
              {/* Original Price (Bulk Discount Price) */}
              {originalPrice > discountedPrice && (
                <p className="text-sm text-gray-500">
                  {currency}{originalPrice.toFixed(2)}
                  <span className="text-xs ml-1">(Bulk Discount Price)</span>
                </p>
              )}
              
              {/* Addons breakdown */}
              {addonsTotalPrice > 0 && (
                <div className="text-xs text-gray-600">
                  <span>Base: {currency}{basePrice.toFixed(2)}</span>
                  <span> + Addons: {currency}{addonsTotalPrice.toFixed(2)}</span>
                </div>
              )}
  
              <p className="text-sm text-gray-600">
                Quantity: {quantity}
              </p>
              
              {/* Total breakdown */}
              {quantity > 1 && (
                <div className="text-sm">
                  <p className="font-medium text-gray-800">
                    Total: {currency}{totalPriceWithQuantity.toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white p-5">
      <h2 className="text-slate-600 font-semibold">
        #{myOrder.orderNumber || "N/A"} ,{" "}
        <span className="pl-1">{myOrder.date || "N/A"}</span>
      </h2>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-slate-600 font-semibold">
            Deliver to: {myOrder.shippingInfo?.name || "N/A"}
          </h2>
          <p>
            <span className="bg-blue-200 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded">
              Home
            </span>
            <span className="text-slate-600 text-sm">
              {myOrder.shippingInfo?.address || ""}{" "}
              {myOrder.shippingInfo?.province || ""}{" "}
              {myOrder.shippingInfo?.city || ""}{" "}
              {myOrder.shippingInfo?.area || ""}
            </span>
          </p>
          <p className="text-slate-600 text-sm font-semibold">
            Email to {userInfo?.email || myOrder?.shippingInfo?.email || "N/A"}
          </p>
        </div>
        <div className="text-slate-600">
          <h2>
            Price: {myOrder.products?.[0]?.currency || "NA"}
            {(() => {
              // Use authororders.price if available, otherwise use calculated price
              if (myOrder.authorOrdersData && myOrder.authorOrdersData.length > 0) {
                // Sum up all authororders prices (these already include shipping)
                const totalAuthorOrdersPrice = myOrder.authorOrdersData.reduce((total, authorOrder) => {
                  return total + (authorOrder.price || 0);
                }, 0);
                return totalAuthorOrdersPrice.toFixed(2);
              } else {
                // Fallback to stored price if no authororders data
                return (myOrder.price || 0).toFixed(2);
              }
            })()} including shipping
          </h2>
          <p>
            Payment status:{" "}
            <span
              className={`py-[1px] text-xs px-3 ${
                myOrder.payment_status === "paid"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              } rounded-md`}
            >
              {myOrder.payment_status || "N/A"}
            </span>
          </p>
          
          {/* Payment Details Section */}
          {myOrder.paymentDetails && myOrder.payment_status === 'paid' && (
            <div className="mt-3 p-3 bg-gray-50 rounded-md">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Payment Details:</h3>
              <div className="space-y-1">
                <p className="text-xs">
                  <span className="font-medium text-gray-600">Payment Method:</span>
                  <span className="ml-2 capitalize">{myOrder.paymentMethod || myOrder.paymentDetails.gateway}</span>
                </p>
                <p className="text-xs">
                  <span className="font-medium text-gray-600">Gateway:</span>
                  <span className="ml-2 capitalize">{myOrder.paymentDetails.gateway}</span>
                </p>
                {myOrder.paymentDetails.transactionId && (
                  <p className="text-xs">
                    <span className="font-medium text-gray-600">Transaction ID:</span>
                    <span className="ml-2 font-mono text-gray-800">{myOrder.paymentDetails.transactionId}</span>
                  </p>
                )}
                {myOrder.paymentDetails.amount && (
                  <p className="text-xs">
                    <span className="font-medium text-gray-600">Amount:</span>
                    <span className="ml-2">{myOrder.products?.[0]?.currency || "NA"} {Number(myOrder.paymentDetails.amount).toFixed(2)}</span>
                  </p>
                )}
                {myOrder.paymentDetails.fee && (
                  <p className="text-xs">
                    <span className="font-medium text-gray-600">Fee:</span>
                    <span className="ml-2">{myOrder.products?.[0]?.currency || "NA"} {Number(myOrder.paymentDetails.fee).toFixed(2)}</span>
                  </p>
                )}
                {myOrder.paymentDetails.netAmount && (
                  <p className="text-xs">
                    <span className="font-medium text-gray-600">Net Amount:</span>
                    <span className="ml-2">{myOrder.products?.[0]?.currency || "NA"} {Number(myOrder.paymentDetails.netAmount).toFixed(2)}</span>
                  </p>
                )}
                {myOrder.paymentDetails.paidAt && (
                  <p className="text-xs">
                    <span className="font-medium text-gray-600">Paid At:</span>
                    <span className="ml-2">{new Date(myOrder.paymentDetails.paidAt).toLocaleString()}</span>
                  </p>
                )}
              </div>
            </div>
          )}
          <p>
            Order status:{" "}
            <span
              className={`py-[1px] text-xs px-3 ${
                myOrder.delivery_status === "delivered"
                  ? "bg-green-100 text-green-800"
                  : myOrder.delivery_status === "cancelled"
                  ? "bg-red-100 text-red-800"
                  : "bg-yellow-100 text-yellow-800"
              } rounded-md`}
            >
              {myOrder.delivery_status || "N/A"}
            </span>
          </p>
          {canCancelOrder && (
            <button
              onClick={handleCancelOrder}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Cancel Order
            </button>
          )}
        </div>
      </div>
      <div className="mt-3">
        <h2 className="text-slate-600 text-lg pb-2">Products</h2>
        <div className="flex gap-5 flex-col">
          {myOrder.products &&
            myOrder.products.map((product, index) => (
              <CartItem key={index} product={product} />
            ))}
        </div>
      </div>
    </div>
  );
};

export default Order;